import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './TutorialRulesScrollHint.css';
import {rulesSectionScrolled} from '../../../redux/actions';
import {selectTutorialState} from '../../../redux/reducers/privateStateReducer/selectors';
import {selectGameType} from '../../../redux/reducers/sharedStateReducer/selectors';
import {getTutorialChapter} from '../../../configs/tutorial';
import {GAME_TYPES} from '../../../constants/game';

/**
 * Подсказка «пролистай страницу» на шаге обучения с проверкой
 * rulesSectionScrolled: рука ходит вверх-вниз с затуханием поверх страницы
 * открытого раздела правил, пока игрок не прокрутит ее вниз.
 *
 * Живет ВНУТРИ модалки правил (а не в общем слое подсказок): оверлей правил
 * перекрывает и обучающие модалки, и панель задач, и стрелку — она на открытых
 * правилах уводится на кнопку «Скрыть». Здесь же считается и сам факт
 * прокрутки: слушатель висит на той же скролл-области, над которой нарисована
 * подсказка.
 */

// Порог прокрутки: половина видимой высоты страницы, но не меньше 150 px и не
// больше того, что вообще можно прокрутить
const MIN_SCROLL_THRESHOLD_PX = 150;
// Настоящий указатель (мышь) — за компьютером крутят колесико, на телефоне и
// планшете листают пальцем: у подсказки разные значок, текст и анимация
const MOUSE_POINTER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const HINTS = {
  mouse: {
    src: '/img/icons/mouse.png',
    alt: 'Колесико мыши',
    label: 'Крути колесико вниз',
    modifier: 'tutorial-rules-scroll-hint_icon-mouse',
  },
  touch: {
    src: '/img/icons/free action.png',
    alt: 'Листание страницы',
    label: 'Листай вниз',
    modifier: 'tutorial-rules-scroll-hint_icon-hand',
  },
};
// Страница короче видимой области (широкий экран, короткий раздел): листать
// нечего — шаг засчитывается сам, но не мгновенно, чтобы подсказка успела
// показаться и игрок понял, что от него хотели
const NOTHING_TO_SCROLL_DELAY_MS = 1200;
const NOTHING_TO_SCROLL_MAX_PX = 8;

const TutorialRulesScrollHint = ({scrollAreaRef, sectionId, isHidden}) => {
  const dispatch = useDispatch();
  const tutorialState = useSelector(selectTutorialState);
  const gameType = useSelector(selectGameType);
  const [box, setBox] = useState(null);
  const [isMousePointer, setIsMousePointer] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOUSE_POINTER_MEDIA_QUERY).matches
  );
  // Засчитываем прокрутку один раз на шаг: экшен и сам дедуплицируется в
  // редьюсере, но лишние диспатчи на каждом событии прокрутки не нужны
  const isCountedRef = useRef(false);

  const chapter = getTutorialChapter(tutorialState.chapterId);
  const activeStep = chapter ? chapter.steps[tutorialState.stepIndex] : null;
  const isStepActive = Boolean(activeStep)
    && !tutorialState.isChapterCompleted
    && activeStep.complete?.check === 'rulesSectionScrolled'
    && activeStep.complete?.sectionId === sectionId
    // В записи прохождения подсказки не показываем: зритель ничего не проходит
    && gameType !== GAME_TYPES.REPLAY;
  const isShown = isStepActive && !isHidden;

  const countScrolled = useCallback(() => {
    if (isCountedRef.current) {
      return;
    }
    isCountedRef.current = true;
    dispatch(rulesSectionScrolled(sectionId));
  }, [dispatch, sectionId]);

  // Новый шаг или другой раздел — считаем прокрутку заново
  useEffect(() => {
    isCountedRef.current = false;
  }, [sectionId, tutorialState.stepIndex]);

  // Подключенная мышь может появиться и исчезнуть посреди главы (планшет с
  // клавиатурой), да и эмуляция устройства в браузере меняет ответ на лету
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const mediaQuery = window.matchMedia(MOUSE_POINTER_MEDIA_QUERY);
    const syncPointer = () => setIsMousePointer(mediaQuery.matches);
    mediaQuery.addEventListener('change', syncPointer);
    return () => mediaQuery.removeEventListener('change', syncPointer);
  }, []);

  // Геометрия скролл-области: подсказка рисуется ровно над страницей раздела
  useEffect(() => {
    const scrollArea = scrollAreaRef?.current;
    if (!isShown || !scrollArea) {
      setBox(null);
      return undefined;
    }

    const syncBox = () => {
      const next = {
        left: scrollArea.offsetLeft,
        top: scrollArea.offsetTop,
        width: scrollArea.offsetWidth,
        height: scrollArea.offsetHeight,
      };
      setBox((prev) => (prev
        && prev.left === next.left && prev.top === next.top
        && prev.width === next.width && prev.height === next.height
        ? prev
        : next));
    };

    syncBox();
    window.addEventListener('resize', syncBox);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncBox) : null;
    observer?.observe(scrollArea);
    return () => {
      window.removeEventListener('resize', syncBox);
      observer?.disconnect();
    };
  }, [isShown, scrollAreaRef]);

  // Сама прокрутка. Слушатель нужен и при закрытой легенде, и при открытой:
  // подсказка спрятана, но прокрутку страницы игроку засчитываем
  useEffect(() => {
    const scrollArea = scrollAreaRef?.current;
    if (!isStepActive || !scrollArea) {
      return undefined;
    }

    const handleScroll = () => {
      const maxScroll = scrollArea.scrollHeight - scrollArea.clientHeight;
      if (maxScroll <= 0) {
        return;
      }
      const threshold = Math.min(Math.max(scrollArea.clientHeight / 2, MIN_SCROLL_THRESHOLD_PX), maxScroll);
      // +1 — на дробные scrollTop при масштабировании страницы
      if (scrollArea.scrollTop + 1 >= threshold) {
        countScrolled();
      }
    };

    // Страницу короче экрана прокрутить нельзя — такой шаг иначе не пройти
    const timeoutId = setTimeout(() => {
      if (scrollArea.scrollHeight - scrollArea.clientHeight <= NOTHING_TO_SCROLL_MAX_PX) {
        countScrolled();
      }
    }, NOTHING_TO_SCROLL_DELAY_MS);

    scrollArea.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      clearTimeout(timeoutId);
      scrollArea.removeEventListener('scroll', handleScroll);
    };
  }, [isStepActive, scrollAreaRef, countScrolled]);

  if (!isShown || !box) {
    return null;
  }

  const hint = isMousePointer ? HINTS.mouse : HINTS.touch;

  return (
    <div
      className={'tutorial-rules-scroll-hint'}
      style={{left: box.left, top: box.top, width: box.width, height: box.height}}
    >
      <img
        className={`tutorial-rules-scroll-hint_icon ${hint.modifier}`}
        src={hint.src}
        alt={hint.alt}
        aria-hidden={'true'}
      />
      <span className={'tutorial-rules-scroll-hint_label'}>{hint.label}</span>
    </div>
  );
};

export default TutorialRulesScrollHint;
